import {sendEvent} from '@github-ui/hydro-analytics'
import {ReposSelector} from '@github-ui/repos-selector'
import {GlobeIcon, RepoIcon} from '@primer/octicons-react'
import {useCallback, useMemo} from 'react'

import {makeRepositoryReference} from '../utils/copilot-chat-helpers'
import {useChatStateValues} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {queryForRepos} from './repo-branch-agent-selector/RepoBranchAgentSelector'

// Minimal repository type for chat dropdown - only includes fields needed for display and selection
interface ChatDropdownRepository {
  id: number
  name: string // nameWithOwner format (owner/repo)
  enabled?: boolean
  private?: boolean
}

/**
 * ChatReposPicker - Repository selection component for Copilot Chat
 * Re-use the repo picker from the Mission Control repo picker component with adjustments to allow multiple selection
 * and loading repositories from the same endpoint as the RepoBranchAgentPicker.
 */
export function ChatReposPicker() {
  const manager = useChatManager()
  const {currentReferences, currentRepository, mode} = useChatStateValues(
    'currentReferences',
    'currentRepository',
    'mode',
  )

  // Get currently selected repository references
  const selectedRepoReferences = currentReferences.filter(r => r.type === 'repository')

  // Map selected references to ChatDropdownRepository format
  const selectedRepos = useMemo(() => {
    const repos: ChatDropdownRepository[] = []
    for (const ref of selectedRepoReferences) {
      repos.push({
        id: ref.id,
        name: `${ref.ownerLogin}/${ref.name}`,
        enabled: true,
        private: ref.visibility !== 'public',
      })
    }
    return repos
  }, [selectedRepoReferences])

  // Repository loader that fetches from the same endpoint as RepoBranchAgentPicker
  // Note: We override 'enabled' to true because chat allows attaching any repo,
  // not just Copilot-enabled ones (unlike Mission Control task mode)
  const repositoryLoader = useCallback(
    async (query: string): Promise<ChatDropdownRepository[]> => {
      const currentRepoNWO = currentRepository ? `${currentRepository.ownerLogin}/${currentRepository.name}` : undefined
      const repos = await queryForRepos(query, undefined, currentRepoNWO)
      // Filter out repos without IDs and map to ChatDropdownRepository format
      return repos
        .filter(repo => repo.id != null)
        .map(repo => ({
          id: repo.id!,
          name: repo.nameWithOwner,
          enabled: true,
          private: repo.private,
        }))
    },
    [currentRepository],
  )

  // Handle repository selection changes
  const handleRepoSelect = useCallback(
    async (repos: ChatDropdownRepository[] | undefined) => {
      if (!repos) {
        // Clear all repository references
        for (const ref of selectedRepoReferences) {
          manager.removeReference(ref)
          sendEvent('dotcom_chat.activate', {
            target: 'ATTACHMENT_MENU_REPOSITORIES',
            action: 'removed',
            component: 'REPOSITORY_PICKER',
            mode,
          })
        }
        return
      }

      const newSelectedIds = new Set(repos.map(r => r.id))

      // Remove deselected repos
      for (const ref of selectedRepoReferences) {
        if (!newSelectedIds.has(ref.id)) {
          manager.removeReference(ref)
          sendEvent('dotcom_chat.activate', {
            target: 'ATTACHMENT_MENU_REPOSITORIES',
            action: 'removed',
            component: 'REPOSITORY_PICKER',
            mode,
          })
        }
      }

      // Add newly selected repos
      for (const repo of repos) {
        const alreadySelected = selectedRepoReferences.some(ref => ref.id === repo.id)
        if (!alreadySelected && repo.id != null) {
          const repoResponse = await manager.service.fetchRepo(repo.id)
          if (repoResponse.ok) {
            const reference = makeRepositoryReference(repoResponse.payload)
            manager.addReference(reference, 'repoMenu')
            sendEvent('dotcom_chat.activate', {
              target: 'ATTACHMENT_MENU_REPOSITORIES',
              action: 'selected',
              component: 'REPOSITORY_PICKER',
              mode,
            })
          }
        }
      }
    },
    [selectedRepoReferences, manager, mode],
  )

  const buttonText = useMemo(() => {
    const count = selectedRepos.length
    if (count === 0) return 'All repositories'
    if (count === 1) return selectedRepos[0]?.name || 'Select repositories'
    return `${count} repositories`
  }, [selectedRepos])

  const buttonIcon = useMemo(() => {
    return selectedRepos.length === 0 ? GlobeIcon : RepoIcon
  }, [selectedRepos.length])

  return (
    <ReposSelector<ChatDropdownRepository>
      repositoryLoader={repositoryLoader}
      selectionVariant="multiple"
      selectAllOption={false}
      onSelect={handleRepoSelect}
      liveSelection
      currentSelection={selectedRepos}
      width="medium"
      buttonText={buttonText}
      additionalButtonProps={{
        leadingVisual: buttonIcon,
        trailingAction: null,
        'aria-label': 'Select repositories to attach to conversation',
      }}
      onOpen={() => {
        sendEvent('dotcom_chat.activate', {
          target: 'ATTACHMENT_MENU_REPOSITORIES',
          action: 'open',
          component: 'REPOSITORY_PICKER',
          mode,
        })
      }}
    />
  )
}

try{ ChatReposPicker.displayName ||= 'ChatReposPicker' } catch {}